/**
 * RHYTHM TRAINER ENGINE V3.0 — Flagship 2027
 * Full-featured rhythm training with subdivisions, groove templates,
 * accuracy scoring, challenge modes, and session history.
 */

class RhythmTrainerEngine {
    constructor() {
        // === STATE ===
        this.isPlaying = false;
        this.bpm = 100;
        this.beatsPerMeasure = 4;
        this.volume = 80;
        this.soundType = 'woodblock';

        // === SUBDIVISION ===
        this.subdivision = 1; // 1=quarter, 2=eighth, 3=triplet, 4=sixteenth
        this.subdivisionLabels = { 1: '♩', 2: '♫', 3: '♪³', 4: '𝅘𝅥𝅯' };

        // === TRAINER MODES ===
        this.mode = 'fixed'; // fixed, progressive, random, groove, survival, mirror
        this.playBars = 2;
        this.muteBars = 1;
        this.randomChance = 50;

        // === GROOVE TEMPLATES ===
        this.grooveType = 'straight';
        this.grooves = {
            straight: {
                name: 'Straight',
                pattern: [1, 0, 1, 0, 1, 0, 1, 0], // 8th note grid: 1=accent, 0=ghost, null=skip
                swing: 0,
                description: 'Even 8th notes, no swing'
            },
            swing: {
                name: 'Swing',
                pattern: [1, null, 0, 1, null, 0, 1, null],
                swing: 0.33,
                description: 'Triplet swing feel'
            },
            shuffle: {
                name: 'Shuffle',
                pattern: [1, null, 0.5, 1, null, 0.5, 1, null],
                swing: 0.5,
                description: 'Heavy blues shuffle'
            },
            half_time: {
                name: 'Half-Time',
                pattern: [1, 0, 0, 0, 0.8, 0, 0, 0],
                swing: 0,
                description: 'Snare on 3, half-time feel'
            },
            samba: {
                name: 'Samba',
                pattern: [1, 0, 0.7, 0, 0.5, 0, 0.8, 0],
                swing: 0,
                description: 'Brazilian 2-bar samba pattern'
            },
            six_eight: {
                name: '6/8 Afro-Cuban',
                pattern: [1, 0, 0.6, 1, 0, 0.6],
                swing: 0,
                description: 'Compound meter, two groups of three'
            },
            bossa: {
                name: 'Bossa Nova',
                pattern: [1, 0, 0, 0.7, 0, 0, 1, 0, 0, 0.7, 0, 0, 0.5, 0, 0, 0.7],
                swing: 0,
                description: 'Syncopated Brazilian pattern'
            },
            funk: {
                name: 'Funk',
                pattern: [1, 0, 0.3, 0, 0, 0.7, 0, 0.5, 0, 0, 0.8, 0, 0, 0.5, 0, 0.3],
                swing: 0,
                description: 'Heavy 16th note funk groove'
            }
        };

        // === ACCENT PATTERN (user-editable) ===
        this.accentPattern = [1, 0.3, 0.5, 0.3]; // per beat accents (quarter note level)
        this.customAccentMode = false;

        // === PROGRESSIVE STATE ===
        this.cycleCount = 0;
        this.level = 1;
        this.currentLevelMute = 1;

        // === COUNT-IN ===
        this.countInBars = 1; // bars to play before training starts
        this.isCountingIn = false;

        // === RUNTIME STATE ===
        this.currentBeat = 0;
        this.currentBarBeat = 0;
        this.currentSubBeat = 0;
        this.totalBarCount = 0;

        // === SCORING ===
        this.inputDeviations = [];
        this.sessionScore = 0;
        this.totalTaps = 0;
        this.perfectCount = 0;
        this.goodCount = 0;
        this.okCount = 0;
        this.missCount = 0;
        this.bestStreak = 0;
        this.currentStreak = 0;
        this.sessionStartTime = 0;

        // === SESSION HISTORY ===
        this.sessionHistory = this.loadHistory();

        // === SURVIVAL MODE ===
        this.survivalLevel = 1;
        this.survivalLives = 3;
        this.survivalBarsPerLevel = 4;

        // === MIRROR MODE ===
        this.mirrorPattern = [];
        this.mirrorPlayback = true;
        this.mirrorUserInput = [];
        this.mirrorLength = 4; // beats

        // === AUDIO ===
        this.ctx = null;
        this.nextNoteTime = 0;
        this.scheduleAheadTime = 0.1;
        this.lookahead = 25;
        this.timerID = null;

        // === EXPECTED BEAT TIMES (for tap accuracy) ===
        this.scheduledBeats = []; // Array of { time, beat, isAccent, isMuted }

        // === CALLBACKS ===
        this.onBeatCallback = null;
        this.onScoreCallback = null;
        this.onSurvivalCallback = null;
        this.onSessionEndCallback = null;
    }

    // ===== INITIALIZATION =====
    init() {
        this.resetProgressive();
        this.resetScore();
    }

    setCallback(onBeat) { this.onBeatCallback = onBeat; }
    setScoreCallback(cb) { this.onScoreCallback = cb; }
    setSurvivalCallback(cb) { this.onSurvivalCallback = cb; }
    setSessionEndCallback(cb) { this.onSessionEndCallback = cb; }

    // ===== SCORE =====
    resetScore() {
        this.inputDeviations = [];
        this.sessionScore = 0;
        this.totalTaps = 0;
        this.perfectCount = 0;
        this.goodCount = 0;
        this.okCount = 0;
        this.missCount = 0;
        this.bestStreak = 0;
        this.currentStreak = 0;
        this.sessionStartTime = Date.now();
    }

    resetProgressive() {
        this.cycleCount = 0;
        this.level = 1;
    }

    getAccuracy() {
        if (this.totalTaps === 0) return 0;
        return Math.round(((this.perfectCount + this.goodCount) / this.totalTaps) * 100);
    }

    getAverageDeviation() {
        if (this.inputDeviations.length === 0) return 0;
        const sum = this.inputDeviations.reduce((a, b) => a + Math.abs(b), 0);
        return Math.round(sum / this.inputDeviations.length);
    }

    getConsistency() {
        if (this.inputDeviations.length < 2) return 100;
        const abs = this.inputDeviations.map(d => Math.abs(d));
        const mean = abs.reduce((a, b) => a + b) / abs.length;
        const variance = abs.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / abs.length;
        const stdDev = Math.sqrt(variance);
        // Convert to 0-100 scale (lower stdDev = higher consistency)
        return Math.max(0, Math.min(100, Math.round(100 - stdDev * 2)));
    }

    getSessionDuration() {
        return Math.round((Date.now() - this.sessionStartTime) / 1000);
    }

    // ===== TAP CHECK =====
    tapCheck() {
        if (!this.isPlaying || !this.ctx || this.isCountingIn) return null;

        const now = this.ctx.currentTime;

        // Find closest expected beat
        let closestBeat = null;
        let closestDist = Infinity;

        for (const beat of this.scheduledBeats) {
            const dist = Math.abs(now - beat.time);
            if (dist < closestDist) {
                closestDist = dist;
                closestBeat = beat;
            }
        }

        if (!closestBeat) return null;

        const diff = (now - closestBeat.time) * 1000; // ms
        const absDiff = Math.abs(diff);

        // Grade
        let grade = 'miss';
        if (absDiff < 20) grade = 'perfect';
        else if (absDiff < 50) grade = 'good';
        else if (absDiff < 100) grade = 'ok';

        // Update score
        this.totalTaps++;
        this.inputDeviations.push(diff);
        if (this.inputDeviations.length > 50) this.inputDeviations.shift();

        // Points
        const pointsMap = { perfect: 100, good: 50, ok: 20, miss: 0 };
        this.sessionScore += pointsMap[grade];

        // Grade counts
        if (grade === 'perfect') { this.perfectCount++; this.currentStreak++; }
        else if (grade === 'good') { this.goodCount++; this.currentStreak++; }
        else if (grade === 'ok') { this.okCount++; this.currentStreak = 0; }
        else { this.missCount++; this.currentStreak = 0; }

        if (this.currentStreak > this.bestStreak) this.bestStreak = this.currentStreak;

        // Survival mode — lose a life on miss
        if (this.mode === 'survival' && grade === 'miss') {
            this.survivalLives--;
            if (this.onSurvivalCallback) this.onSurvivalCallback({ lives: this.survivalLives, level: this.survivalLevel });
            if (this.survivalLives <= 0) {
                this.endSession('Game Over!');
                return { diff, grade, gameOver: true };
            }
        }

        // Score callback
        if (this.onScoreCallback) {
            this.onScoreCallback({
                score: this.sessionScore,
                accuracy: this.getAccuracy(),
                streak: this.currentStreak,
                bestStreak: this.bestStreak,
                avgDeviation: this.getAverageDeviation(),
                consistency: this.getConsistency()
            });
        }

        // Beat callback for visual feedback
        if (this.onBeatCallback) {
            this.onBeatCallback(null, false, false, now, { type: 'hit', diff, grade });
        }

        return { diff, grade };
    }

    // ===== AUDIO ENGINE =====
    initAudio() {
        if (!this.ctx) {
            this.ctx = new (window.AudioContext || window.webkitAudioContext)();
        }
        if (this.ctx.state === 'suspended') this.ctx.resume();
    }

    playClick(time, isAccent = false, isMuted = false, velocity = 1.0) {
        if (isMuted) return;

        const vol = (this.volume / 100) * velocity;
        if (vol < 0.01) return;

        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        const noise = this.ctx.createBufferSource();
        const noiseGain = this.ctx.createGain();
        const filter = this.ctx.createBiquadFilter();

        osc.connect(gain);
        gain.connect(this.ctx.destination);

        // White Noise
        const noiseBuffer = this.ctx.createBuffer(1, this.ctx.sampleRate * 0.01, this.ctx.sampleRate);
        const noiseData = noiseBuffer.getChannelData(0);
        for (let i = 0; i < noiseData.length; i++) noiseData[i] = Math.random() * 2 - 1;
        noise.buffer = noiseBuffer;

        filter.type = 'highpass';
        filter.frequency.value = 5000;
        noise.connect(filter);
        filter.connect(noiseGain);
        noiseGain.connect(this.ctx.destination);

        switch (this.soundType) {
            case 'digital':
                osc.type = 'triangle';
                osc.frequency.setValueAtTime(isAccent ? 1000 : 600, time);
                gain.gain.setValueAtTime(vol * 0.8, time);
                gain.gain.exponentialRampToValueAtTime(0.001, time + 0.08);
                noiseGain.gain.setValueAtTime(vol * 0.1, time);
                noiseGain.gain.exponentialRampToValueAtTime(0.001, time + 0.02);
                osc.start(time); osc.stop(time + 0.08);
                noise.start(time); noise.stop(time + 0.08);
                break;

            case 'rimshot': {
                osc.type = 'sine';
                osc.frequency.setValueAtTime(isAccent ? 500 : 400, time);
                gain.gain.setValueAtTime(vol, time);
                gain.gain.exponentialRampToValueAtTime(0.001, time + 0.05);

                const popOsc = this.ctx.createOscillator();
                const popGain = this.ctx.createGain();
                popOsc.connect(popGain); popGain.connect(this.ctx.destination);
                popOsc.frequency.setValueAtTime(200, time);
                popOsc.frequency.exponentialRampToValueAtTime(10, time + 0.05);
                popGain.gain.setValueAtTime(vol, time);
                popGain.gain.exponentialRampToValueAtTime(0.001, time + 0.05);
                noiseGain.gain.setValueAtTime(vol * 0.4, time);
                noiseGain.gain.exponentialRampToValueAtTime(0.001, time + 0.02);
                osc.start(time); osc.stop(time + 0.05);
                popOsc.start(time); popOsc.stop(time + 0.05);
                noise.start(time); noise.stop(time + 0.05);
                break;
            }

            case 'woodblock': {
                osc.type = 'sine';
                osc.frequency.setValueAtTime(isAccent ? 880 : 600, time);
                gain.gain.setValueAtTime(vol, time);
                gain.gain.exponentialRampToValueAtTime(0.001, time + 0.08);

                const osc2 = this.ctx.createOscillator();
                const gain2 = this.ctx.createGain();
                osc2.connect(gain2); gain2.connect(this.ctx.destination);
                osc2.type = 'square';
                osc2.frequency.setValueAtTime((isAccent ? 880 : 600) * 1.48, time);
                gain2.gain.setValueAtTime(vol * 0.2, time);
                gain2.gain.exponentialRampToValueAtTime(0.001, time + 0.04);
                osc.start(time); osc.stop(time + 0.08);
                osc2.start(time); osc2.stop(time + 0.08);
                break;
            }

            case 'shaker':
                filter.frequency.value = isAccent ? 6000 : 4000;
                filter.Q.value = 5;
                noiseGain.gain.setValueAtTime(vol * 0.4, time);
                noiseGain.gain.exponentialRampToValueAtTime(0.001, time + (isAccent ? 0.08 : 0.04));
                noise.start(time); noise.stop(time + 0.1);
                break;

            case 'clave': {
                osc.type = 'sine';
                osc.frequency.setValueAtTime(isAccent ? 2500 : 2000, time);
                gain.gain.setValueAtTime(vol * 0.6, time);
                gain.gain.exponentialRampToValueAtTime(0.001, time + 0.03);
                osc.start(time); osc.stop(time + 0.04);
                break;
            }

            case 'cowbell': {
                osc.type = 'square';
                osc.frequency.setValueAtTime(isAccent ? 800 : 560, time);
                gain.gain.setValueAtTime(vol * 0.5, time);
                gain.gain.exponentialRampToValueAtTime(0.001, time + 0.12);
                const osc2b = this.ctx.createOscillator();
                const gain2b = this.ctx.createGain();
                osc2b.connect(gain2b); gain2b.connect(this.ctx.destination);
                osc2b.type = 'square';
                osc2b.frequency.setValueAtTime((isAccent ? 800 : 560) * 1.5, time);
                gain2b.gain.setValueAtTime(vol * 0.3, time);
                gain2b.gain.exponentialRampToValueAtTime(0.001, time + 0.08);
                osc.start(time); osc.stop(time + 0.12);
                osc2b.start(time); osc2b.stop(time + 0.12);
                break;
            }
        }
    }

    // ===== SCHEDULER =====
    start() {
        this.initAudio();
        this.isPlaying = true;
        this.isCountingIn = this.countInBars > 0;
        this._countInBarsDone = 0;
        this.currentBeat = 0;
        this.currentBarBeat = 0;
        this.currentSubBeat = 0;
        this.totalBarCount = 0;
        this.scheduledBeats = [];
        this.resetProgressive();
        this.resetScore();

        // Survival mode init
        if (this.mode === 'survival') {
            this.survivalLevel = 1;
            this.survivalLives = 3;
        }

        // Mirror mode init
        if (this.mode === 'mirror') {
            this.generateMirrorPattern();
        }

        this.nextNoteTime = this.ctx.currentTime + 0.1;
        this.scheduler();
    }

    stop() {
        this.isPlaying = false;
        if (this.timerID) {
            clearTimeout(this.timerID);
            this.timerID = null;
        }
    }

    toggle() {
        if (this.isPlaying) {
            this.endSession();
        } else {
            this.start();
        }
    }

    endSession(reason = 'Session Complete') {
        this.stop();

        const sessionData = {
            date: new Date().toISOString(),
            mode: this.mode,
            bpm: this.bpm,
            subdivision: this.subdivision,
            beatsPerMeasure: this.beatsPerMeasure,
            duration: this.getSessionDuration(),
            score: this.sessionScore,
            totalTaps: this.totalTaps,
            accuracy: this.getAccuracy(),
            avgDeviation: this.getAverageDeviation(),
            consistency: this.getConsistency(),
            bestStreak: this.bestStreak,
            perfectCount: this.perfectCount,
            goodCount: this.goodCount,
            okCount: this.okCount,
            missCount: this.missCount,
            reason
        };

        // Save to history
        this.sessionHistory.push(sessionData);
        if (this.sessionHistory.length > 50) this.sessionHistory.shift();
        this.saveHistory();

        if (this.onSessionEndCallback) this.onSessionEndCallback(sessionData);
    }

    scheduler() {
        if (!this.isPlaying) return;
        while (this.nextNoteTime < this.ctx.currentTime + this.scheduleAheadTime) {
            this.scheduleNote(this.currentBarBeat, this.currentSubBeat, this.nextNoteTime);
            this.nextNote();
        }
        if (this.isPlaying) {
            this.timerID = setTimeout(() => this.scheduler(), this.lookahead);
        }
    }

    scheduleNote(beatNumber, subBeat, time) {
        // === COUNT-IN ===
        // Track bar transitions during count-in
        if (this.isCountingIn && subBeat === 0 && beatNumber === 0 && this.totalBarCount > this._countInBarsDone) {
            this._countInBarsDone = this.totalBarCount;
            if (this._countInBarsDone >= this.countInBars) {
                this.isCountingIn = false;
            }
        }

        let isMuted = false;
        const isMainBeat = subBeat === 0;
        let isAccent = beatNumber === 0 && isMainBeat;
        let velocity = 1.0;

        // === CUSTOM ACCENT PATTERN ===
        if (this.customAccentMode && isMainBeat && this.accentPattern[beatNumber] !== undefined) {
            velocity = this.accentPattern[beatNumber];
            isAccent = velocity > 0.7;
        }

        // === SUBDIVISION VELOCITY ===
        if (!isMainBeat) {
            velocity *= 0.5; // Sub-beats are quieter
            isAccent = false;
        }

        // === GROOVE MODE ===
        if (this.mode === 'groove') {
            const groove = this.grooves[this.grooveType];
            if (groove) {
                const patternIdx = (beatNumber * this.subdivision + subBeat) % groove.pattern.length;
                const patternVal = groove.pattern[patternIdx];
                if (patternVal === null) {
                    isMuted = true;
                } else {
                    velocity = patternVal;
                    isAccent = patternVal > 0.7;
                }
            }
        }

        // === TRAINER LOGIC (Fixed / Progressive) ===
        if (this.mode === 'fixed' || this.mode === 'progressive') {
            let effectiveMuteBars = this.muteBars;

            if (this.mode === 'progressive') {
                const levelAdd = Math.floor(this.cycleCount / 4);
                effectiveMuteBars = this.muteBars + levelAdd;
                this.currentLevelMute = effectiveMuteBars;
                this.level = levelAdd + 1;
            }

            const cycleLength = this.playBars + effectiveMuteBars;
            const currentCyclePos = this.totalBarCount % cycleLength;
            this.cycleCount = Math.floor(this.totalBarCount / cycleLength);

            if (currentCyclePos >= this.playBars) {
                isMuted = true;
            }
        }

        // === RANDOM MODE ===
        if (this.mode === 'random') {
            if (Math.random() * 100 < this.randomChance) {
                isMuted = true;
            }
        }

        // === SURVIVAL MODE ===
        if (this.mode === 'survival') {
            // Every N bars, increase difficulty
            const barsInLevel = this.totalBarCount % this.survivalBarsPerLevel;
            const survivalLevel = Math.floor(this.totalBarCount / this.survivalBarsPerLevel) + 1;

            if (survivalLevel !== this.survivalLevel) {
                this.survivalLevel = survivalLevel;
                // Increase BPM every level
                this.bpm = Math.min(300, this.bpm + 5);
                if (this.onSurvivalCallback) {
                    this.onSurvivalCallback({ lives: this.survivalLives, level: this.survivalLevel, bpm: this.bpm });
                }
            }

            // Mute more as levels progress
            if (survivalLevel > 2 && Math.random() < 0.3 * (survivalLevel - 2)) {
                isMuted = true;
            }
        }

        // === MIRROR MODE ===
        if (this.mode === 'mirror') {
            // Alternate: play pattern, then mute for user input
            const mirrorCyclePos = this.totalBarCount % 2;
            if (mirrorCyclePos === 1) {
                isMuted = true; // User's turn
            }
        }

        // During count-in: play full-volume click, skip muting, don't accept taps
        const isCountIn = this.isCountingIn;
        const effectiveMuted = isCountIn ? false : isMuted;

        // Play the click
        this.playClick(time, isAccent, effectiveMuted, velocity);

        // Track scheduled beats for tap accuracy (only main beats, not during count-in)
        if (isMainBeat && !isCountIn) {
            this.scheduledBeats.push({ time, beat: beatNumber, isAccent, isMuted });
            // Keep only recent beats
            if (this.scheduledBeats.length > 32) this.scheduledBeats.shift();
        }

        // Visual Callback
        if (this.onBeatCallback) {
            this.onBeatCallback(beatNumber, isAccent, effectiveMuted, time, {
                level: this.mode === 'progressive' ? this.currentLevelMute : this.survivalLevel,
                subBeat,
                subdivision: this.subdivision,
                velocity,
                groove: this.mode === 'groove' ? this.grooveType : null,
                isMainBeat,
                survivalLives: this.survivalLives,
                isCountIn,
                countInBar: isCountIn ? this._countInBarsDone + 1 : null
            });
        }
    }

    nextNote() {
        const secondsPerBeat = 60.0 / this.bpm;
        const subBeatDuration = secondsPerBeat / this.subdivision;

        // Apply swing if in groove mode
        let swingOffset = 0;
        if (this.mode === 'groove') {
            const groove = this.grooves[this.grooveType];
            if (groove && groove.swing > 0 && this.currentSubBeat % 2 === 1) {
                swingOffset = subBeatDuration * groove.swing;
            }
        }

        this.nextNoteTime += subBeatDuration + swingOffset;

        this.currentSubBeat++;
        if (this.currentSubBeat >= this.subdivision) {
            this.currentSubBeat = 0;
            this.currentBeat++;
            this.currentBarBeat++;

            if (this.currentBarBeat >= this.beatsPerMeasure) {
                this.currentBarBeat = 0;
                this.totalBarCount++;
            }
        }
    }

    // ===== MIRROR MODE =====
    generateMirrorPattern() {
        this.mirrorPattern = [];
        for (let i = 0; i < this.mirrorLength; i++) {
            this.mirrorPattern.push(Math.random() > 0.3 ? 1 : 0);
        }
    }

    // ===== SESSION HISTORY =====
    loadHistory() {
        try {
            const data = window.flPreferenceStorage.getItem('sa-tool-rhythm-trainer');
            return data ? JSON.parse(data) : [];
        } catch (e) {
            return [];
        }
    }

    saveHistory() {
        try {
            window.flPreferenceStorage.setItem('sa-tool-rhythm-trainer', JSON.stringify(this.sessionHistory));
        } catch (e) {
            // Storage full or unavailable
        }
    }

    getHistoryStats() {
        if (this.sessionHistory.length === 0) return null;
        const last10 = this.sessionHistory.slice(-10);
        return {
            totalSessions: this.sessionHistory.length,
            avgAccuracy: Math.round(last10.reduce((a, s) => a + s.accuracy, 0) / last10.length),
            avgDeviation: Math.round(last10.reduce((a, s) => a + s.avgDeviation, 0) / last10.length),
            bestScore: Math.max(...this.sessionHistory.map(s => s.score)),
            bestStreak: Math.max(...this.sessionHistory.map(s => s.bestStreak)),
            totalPracticeTime: this.sessionHistory.reduce((a, s) => a + s.duration, 0),
            improvementTrend: this.getImprovementTrend()
        };
    }

    getImprovementTrend() {
        if (this.sessionHistory.length < 4) return 'neutral';
        const recent = this.sessionHistory.slice(-3);
        const older = this.sessionHistory.slice(-6, -3);
        if (older.length === 0) return 'neutral';
        const recentAvg = recent.reduce((a, s) => a + s.accuracy, 0) / recent.length;
        const olderAvg = older.reduce((a, s) => a + s.accuracy, 0) / older.length;
        if (recentAvg > olderAvg + 5) return 'improving';
        if (recentAvg < olderAvg - 5) return 'declining';
        return 'stable';
    }

    clearHistory() {
        this.sessionHistory = [];
        window.flPreferenceStorage.removeItem('sa-tool-rhythm-trainer');
    }

    // ===== SETTINGS PERSISTENCE =====
    static get SETTINGS_KEY() { return 'sa-tool-rhythm-trainer-settings'; }

    saveSettings() {
        try {
            const s = {
                bpm: this.bpm,
                beatsPerMeasure: this.beatsPerMeasure,
                subdivision: this.subdivision,
                mode: this.mode,
                playBars: this.playBars,
                muteBars: this.muteBars,
                randomChance: this.randomChance,
                grooveType: this.grooveType,
                soundType: this.soundType,
                accentPattern: this.accentPattern
            };
            window.flPreferenceStorage.setItem(RhythmTrainerEngine.SETTINGS_KEY, JSON.stringify(s));
        } catch (e) { /* storage unavailable */ }
    }

    loadSettings() {
        try {
            const raw = window.flPreferenceStorage.getItem(RhythmTrainerEngine.SETTINGS_KEY);
            if (!raw) return;
            const s = JSON.parse(raw);
            if (s.bpm && s.bpm >= 20 && s.bpm <= 300) this.bpm = s.bpm;
            if ([3,4,5,6,7].includes(s.beatsPerMeasure)) this.beatsPerMeasure = s.beatsPerMeasure;
            if ([1,2,3,4].includes(s.subdivision)) this.subdivision = s.subdivision;
            if (['fixed','progressive','random','groove','survival','mirror'].includes(s.mode)) this.mode = s.mode;
            if (s.playBars >= 1 && s.playBars <= 16) this.playBars = s.playBars;
            if (s.muteBars >= 1 && s.muteBars <= 16) this.muteBars = s.muteBars;
            if (s.randomChance >= 0 && s.randomChance <= 100) this.randomChance = s.randomChance;
            if (s.grooveType && this.grooves[s.grooveType]) this.grooveType = s.grooveType;
            if (['woodblock','rimshot','digital','shaker','clave','cowbell'].includes(s.soundType)) this.soundType = s.soundType;
            if (Array.isArray(s.accentPattern) && s.accentPattern.length >= 3) this.accentPattern = s.accentPattern;
        } catch (e) { /* corrupt data, ignore */ }
    }

    // ===== AUDIO SUSPEND (page hide) =====
    suspendAudio() {
        if (this.isPlaying) this.endSession('page-hidden');
        if (this.ctx && this.ctx.state === 'running') this.ctx.suspend();
    }
}

window.RhythmTrainerEngine = RhythmTrainerEngine;
